import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Pagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Search from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllDealersAction,getAllDistrictsAction,getAllMandalsAction } from '../../store/actions';
const Dealer = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const theme = useTheme(); 
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { successData: dealersData, loading } = useSelector((state) => state.getAllDealersReducer || {});
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});

  // Normalize dealers array from possible shapes
  const dealerRows = useMemo(() => {
    if (Array.isArray(dealersData?.data)) return dealersData.data;
    if (Array.isArray(dealersData?.data?.dealers)) return dealersData.data.dealers;
    if (Array.isArray(dealersData)) return dealersData;
    return [];
  }, [dealersData]);

  // Normalize districts list for dropdown options
  const rawToArray = (data, innerKey) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (innerKey && data?.data && Array.isArray(data.data[innerKey])) return data.data[innerKey];
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const districtsRaw = useMemo(() => rawToArray(districtsData, 'districts'), [districtsData]);
  const districtOptions = useMemo(() => districtsRaw.map(d => ({
    id: d?.districtId || d?.id || d?._id || d?.value,
    label: d?.name || d?.districtName || d?.label || '',
  })).filter(d => d.id && d.label), [districtsRaw]);

  // Build quick lookup maps for names
  const districtMap = useMemo(() => {
    const map = {};
    districtOptions.forEach(d => { if (d.id && d.label) map[String(d.id)] = d.label; });
    return map;
  }, [districtOptions]);

  const mandalsRaw = useMemo(() => rawToArray(mandalsData, 'mandals'), [mandalsData]);
  const mandalMap = useMemo(() => {
    const map = {};
    mandalsRaw.forEach(m => {
      const id = m?.mandalId || m?.id || m?._id || m?.value;
      const label = m?.name || m?.mandalName || m?.label;
      if (id && label) map[String(id)] = label;
    });
    return map;
  }, [mandalsRaw]);

  // Apply filters to raw dealer rows, then map for display
  const filteredDealerRows = useMemo(() => {
    const statusFilter = selectedStatus !== 'All' ? String(selectedStatus).toUpperCase() : null;
    return dealerRows.filter((row) => {
      const rowStatus = (() => {
        if (row.status === 'ACTIVE' || row.status === 'INACTIVE' || row.status === 'PENDING') return row.status;
        if (row.active === true) return 'ACTIVE';
        if (row.active === false) return 'INACTIVE';
        return String(row.status || '').toUpperCase();
      })();
      const matchesStatus = !statusFilter || rowStatus === statusFilter;
      const matchesDistrict = selectedDistrictId === 'All' || String(row.districtId) === String(selectedDistrictId);
      return matchesStatus && matchesDistrict;
    });
  }, [dealerRows, selectedStatus, selectedDistrictId]);

  // Prepare display-ready rows once and reuse for all layouts
  const displayRows = useMemo(() => {
    return filteredDealerRows.map((row) => {
      const districtName = row.districtName || row.district?.name || districtMap[String(row.districtId)] || row.districtId || '-';
      const mandalName = row.mandalName || row.mandal?.name || mandalMap[String(row.mandalId)] || row.mandalId || '-';
      const villageName = (row.village && row.village.name) ? row.village.name : (row.villageName || row.villageId || '-');
      return ({
        id: row.dealerId || '',
        name: row.name || '-',
        district: districtName,
        mandal: mandalName,
        village: villageName,
        status: (row.status === 'ACTIVE' || row.active === true) ? 'Active' : (row.status === 'PENDING' ? 'Pending' : 'Inactive'),
      });
    });
  }, [filteredDealerRows, districtMap, mandalMap]);

  // Filter rows by search query across key fields
  const filteredRows = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return displayRows;
    return displayRows.filter(({ id, name, district, mandal, village, status }) => {
      return [id, name, district, mandal, village, status]
        .map((v) => String(v || '').toLowerCase())
        .some((val) => val.includes(q));
    });
  }, [displayRows, searchQuery]);

  useEffect(() => { setPage(1); }, [searchQuery, selectedDistrictId, selectedStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    dispatch(getAllDealersAction({ endPoint: 'dealer' }));
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
  }, [dispatch]);

  const handleEditDealer = (dealerId) => {
    if (!dealerId) return;
    navigate('/masterData/createDealer', { state: { dealerId, mode: 'edit' } });
  };

  // Consistent desktop grid column widths for perfect alignment
  const gridColsDesktop = '0.9fr 1.6fr 1.2fr 1.2fr 1.2fr 0.9fr 0.8fr';


  const handleAddDealer = () => {
    navigate('/masterData/createDealer');
  };

  const getStatusChipStyles = (status) => {
    switch (status) {
      case 'Active':
        return { backgroundColor: '#D1FAE5', color: '#047857' }; 
      case 'Pending':
        return { backgroundColor: '#FFEFD6', color: '#B45309' };
      case 'Inactive':
        return { backgroundColor: '#FEE2E2', color: '#B91C1C' }; 
      default:
        return {};
    }
  };

  return (

    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* PAGE HEADER */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: { sm: 7, md: 0,  } }}>
        Dealer Management
      </Typography>

      {/* FILTER SECTION */}
      <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
            <TextField
              size="small"
              placeholder="Search ..."
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                },
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
          </Grid>



          <Grid item size={{ xs: 6, sm: 3, md: 3 }}>
            <Select
              size="small"
              value={selectedDistrictId}
              onChange={(e) => setSelectedDistrictId(e.target.value)}
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="All">All Districts</MenuItem>
              {districtOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </Select>
          </Grid>



          <Grid item size={{ xs: 6, sm: 3, md: 3 }}>
            <Select
              size="small"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="All">All Statuses</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </Grid>

          <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                whiteSpace: 'nowrap',
                fontSize: { sm: '0.70rem',md: '0.90rem' },
              }}
              fullWidth
              onClick={handleAddDealer}
            >
              + Add New Dealer
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* CONTENT AREA */}
      <Box sx={{ mt: 3 }}>
        {isMobile ? (
          <Grid container spacing={2}>
            {loading && (
              <Grid item size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">Loading dealers...</Typography>
              </Grid>
            )}
            {!loading && pagedRows.map(({ id, name, district, mandal, village, status }) => (
              <Grid item size={{ xs: 12 }} key={id || name}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: 1.5,
                    border: '1px solid #E5E7EB',
                    '&:hover': {
                      border: '2px solid #2563EB',
                      boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                    },
                  }}
                >
                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontSize={11} color="text.secondary" minWidth={88}>Dealer ID:</Typography>
                    <Typography fontSize={12}>{id}</Typography>
                    <Box ml="auto">
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          ...getStatusChipStyles(status),
                          height: 24,
                          '& .MuiChip-label': { fontSize: 11, fontWeight: 600 },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontSize={11} color="text.secondary" minWidth={88}>Dealer Name:</Typography>
                    <Typography fontSize={12}>{name}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontSize={11} color="text.secondary" minWidth={88}>District:</Typography>
                    <Typography fontSize={12}>{district}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontSize={11} color="text.secondary" minWidth={88}>Mandal:</Typography>
                    <Typography fontSize={12}>{mandal}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontSize={11} color="text.secondary" minWidth={88}>Village:</Typography>
                    <Typography fontSize={12}>{village}</Typography>
                  </Box>



                  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={1}>
                    <IconButton size="small" onClick={() => handleEditDealer(id)}>
                      <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
            {!loading && filteredRows.length === 0 && (
              <Grid item size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">No dealers found.</Typography>
              </Grid>
            )}
            {!loading && totalPages > 1 && (
              <Grid item size={{ xs: 12 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                  <Typography variant="body2" >
                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                  </Typography>
                  <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Desktop header row */}
            <Box
              elevation={0}
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: { sm: gridColsDesktop },
                alignItems: 'center',
                px: 2,
                py: 2,
                columnGap: 0,
              }}
            >
              {['Dealer ID', 'Dealer Name', 'District', 'Mandal', 'Village', 'Status', 'Actions'].map((h) => (
                <Typography
                  key={h}
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Desktop value rows */}
            {loading && (
              <Typography variant="body2" color="text.secondary">Loading dealers...</Typography>
            )}
            {!loading && pagedRows.map(({ id, name, district, mandal, village, status }) => (
              <Paper
                key={id || name}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  display: { xs: 'none', sm: 'grid' },
                  gridTemplateColumns: { sm: gridColsDesktop },
                  alignItems: 'center',
                  border: '1px solid #E5E7EB',
                  px: 2,
                  py: 1.5,
                  gap: 0,
                  columnGap: 0,
                  '&:hover': {
                    border: '2px solid #2563EB',
                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                  },
                }}
              >
                <Typography variant="body2">{id}</Typography>
                <Typography variant='body2'>{name}</Typography>
                <Typography variant="body2">{district}</Typography>
                <Typography variant="body2">{mandal}</Typography>
                <Typography variant="body2">{village}</Typography>

                <Chip
                  label={status}
                  size="small"
                  sx={{
                    width: 90,
                    height: 24,
                    ml: -2,
                    ...getStatusChipStyles(status),
                    '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                  }}
                />

                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                  <IconButton size="small" aria-label="edit" onClick={() => handleEditDealer(id)}>
                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                  </IconButton>
                </Box>
              </Paper>
            ))}
            {!loading && filteredRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">No dealers found.</Typography>
            )}
            {!loading && totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                <Typography variant="body2" >
                  Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                </Typography>
                <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Dealer;
